import { CartUpdatedEvent } from '@theme/events';

/**
 * Choix d'envoi quand le panier contient un coffret en précommande (trousse d'Emma et/ou box de Mary)
 * + d'autres produits.
 * - Option « ensemble » : attribut de panier « Tout ensemble à partir du … » par coffret présent, pas de frais.
 * - Option « deux » : attribut « En deux envois » par coffret présent + produit de frais à 0 € (quantité 1) :
 *   variante A si les autres produits sont sous le seuil, variante B sinon. Chaque variante a son
 *   profil de livraison : Shopify ajoute les frais du 2e colis au checkout selon le mode choisi.
 * - Attributs : « Livraison trousse Emma » (trousse), « Livraison box Mary » (box) ; lus par les Flows.
 * - À chaque rendu, le bloc remet le panier en cohérence (coffret retiré, seuil franchi, frais modifiés).
 */
class EnvoiTrousse extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => this.#apply(radio.value));
    });
    this.#reconcile();
  }

  #wanted(choix) {
    const d = this.dataset;
    const show = d.show === 'true';
    const has1 = d.has1 === 'true';
    const has2 = d.has2 === 'true';
    const deux = show && choix === 'deux';
    const below = d.othersBelow === 'true';
    return {
      a: deux && below ? 1 : 0,
      b: deux && !below ? 1 : 0,
      attr: show && has1 ? (deux ? d.valeurDeux : d.valeurEnsemble) : '',
      attr2: show && has2 ? (deux ? d.valeurDeux : d.valeurEnsemble2) : '',
    };
  }

  #currentChoice() {
    const d = this.dataset;
    const courant = d.has1 === 'true' ? d.choix : d.choix2;
    return courant === d.valeurDeux ? 'deux' : 'ensemble';
  }

  #reconcile() {
    const d = this.dataset;
    if (!d.feeA || !d.feeB) return;
    const want = this.#wanted(this.#currentChoice());
    const qa = parseInt(d.qtyA || '0', 10);
    const qb = parseInt(d.qtyB || '0', 10);
    const attrNow = d.choix || '';
    const attr2Now = d.choix2 || '';
    if (qa !== want.a || qb !== want.b || attrNow !== want.attr || attr2Now !== want.attr2) this.#update(want);
  }

  #apply(choix) {
    this.#update(this.#wanted(choix));
  }

  async #update(want) {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('is-loading');

    const d = this.dataset;
    const drawer = this.closest('cart-drawer');
    const attributes = { [d.cle]: want.attr };
    if (d.cle2) attributes[d.cle2] = want.attr2;
    const body = {
      updates: { [d.feeA]: want.a, [d.feeB]: want.b },
      attributes,
    };
    if (drawer) {
      body.sections = drawer.dataset.sectionId;
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'cart update failed');

      if (drawer) {
        document.dispatchEvent(
          new CartUpdatedEvent({}, { itemCount: cart.item_count, source: 'envoi-trousse', sections: cart.sections }),
        );
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('[envoi-trousse]', error);
      this.classList.remove('is-loading');
    } finally {
      this.busy = false;
    }
  }
}

if (!customElements.get('envoi-trousse')) {
  customElements.define('envoi-trousse', EnvoiTrousse);
}
